<template>
  <el-card class="container" shadow="never">
      <div class="mall-invoice-apply-page">
        <div class="">
          <div class="desciconLarge"></div>
          <span class="font18">耗材开票</span>
        </div>
    
        <section v-loading="pageLoading" class="panel form-panel">
          <el-form
            ref="invoiceForm"
            :key="`rules-${form.invoiceType}`"
            :model="form"
            :rules="formRules"
            label-width="120px"
            class="invoice-form"
            @submit.native.prevent
          >
            <div class="form-section">
              <span class="section-label">开票金额</span>
              <span class="amount-display">￥{{ invoiceAmountText }}</span>
            </div>
    
            <div class="form-section">
              <span class="section-label">发票类型</span>
              <el-radio-group v-model="form.invoiceType" class="pill-radio" @change="onInvoiceTypeChange">
                <el-radio-button :label="1">普通发票</el-radio-button>
                <el-radio-button :label="2">专用发票</el-radio-button>
              </el-radio-group>
            </div>
    
            <div class="form-section block">
              <span class="section-label align-top">发票抬头及地址</span>
              <div class="section-body">
                <el-radio-group v-model="form.ifDefault" class="pill-radio">
                  <el-radio-button :label="1" :disabled="!hasDefaultInvoiceHead">使用默认抬头</el-radio-button>
                  <el-radio-button :label="2">填写发票抬头</el-radio-button>
                </el-radio-group>
    
                <div class="form-grid">
                  <el-form-item label="发票抬头" prop="invoiceTitle" class="grid-item">
                    <el-input v-model.trim="form.invoiceTitle" placeholder="请输入发票抬头" maxlength="200" show-word-limit></el-input>
                  </el-form-item>
                  <el-form-item label="企业税号" prop="enterpriseTaxNumber" class="grid-item">
                    <el-input v-model.trim="form.enterpriseTaxNumber" placeholder="请输入企业税号" maxlength="30"></el-input>
                  </el-form-item>
    
                  <el-form-item label="开户行名称" prop="openBankName" class="grid-item">
                    <el-input v-model.trim="form.openBankName" placeholder="请输入开户行名称" maxlength="100"></el-input>
                  </el-form-item>
                  <el-form-item label="开户行账号" prop="openBankAccount" class="grid-item">
                    <el-input v-model.trim="form.openBankAccount" placeholder="请输入开户行账号" maxlength="30"></el-input>
                  </el-form-item>
    
                  <el-form-item label="注册地址" prop="registeredAddress" class="grid-item">
                    <el-input v-model.trim="form.registeredAddress" placeholder="请输入注册地址" maxlength="200"></el-input>
                  </el-form-item>
                  <el-form-item label="注册电话" prop="registeredTelephone" class="grid-item">
                    <el-input v-model.trim="form.registeredTelephone" placeholder="请输入注册电话" maxlength="20"></el-input>
                  </el-form-item>
    
                  <el-form-item label="收件人姓名" prop="addressee" class="grid-item">
                    <el-input v-model.trim="form.addressee" placeholder="请输入收件人姓名" maxlength="50"></el-input>
                  </el-form-item>
                  <el-form-item label="收件地址" prop="deliveryAddress" class="grid-item">
                    <el-input v-model.trim="form.deliveryAddress" placeholder="请输入收件地址" maxlength="200"></el-input>
                  </el-form-item>
    
                  <el-form-item label="收件邮箱" prop="receivingContact" class="grid-item">
                    <el-input v-model.trim="form.receivingContact" placeholder="请输入收件邮箱" maxlength="80"></el-input>
                  </el-form-item>
                  <el-form-item label="收件联系方式" prop="receivingContactInformation" class="grid-item">
                    <el-input v-model.trim="form.receivingContactInformation" placeholder="请输入收件联系方式" maxlength="20"></el-input>
                  </el-form-item>
                </div>
              </div>
            </div>
    
            <div class="footer-actions">
              <el-button class="ghost-btn" @click="goBack">返回</el-button>
              <el-button type="primary" :loading="submitting" @click="handleSubmit">提交</el-button>
            </div>
          </el-form>
        </section>
      </div>
  </el-card>
</template>

<script>
import { getClient } from '@/plugins/auth'
import { listInvoiceHead, submitMallOrderInvoice } from '@/api/request'

const REG_ENTERPRISE_TAX_NUMBER = /^([0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}|[0-9A-Z]{15,20})$/i
const REG_BANK_ACCOUNT = /^\d{8,30}$/
const REG_REGISTERED_PHONE = /^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8}(-\d{1,6})?|400-?\d{3}-?\d{4})$/
const REG_EMAIL = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const REG_MOBILE = /^1[3-9]\d{9}$/

function parseClient() {
  try {
    return JSON.parse(getClient() || '{}')
  } catch (error) {
    return {}
  }
}

function toSnakeCaseKey(key) {
  return String(key || '').replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
}

function extractList(payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  if (typeof payload !== 'object') return []
  if (Array.isArray(payload.rows)) return payload.rows
  if (Array.isArray(payload.records)) return payload.records
  if (Array.isArray(payload.list)) return payload.list
  const data = Object.prototype.hasOwnProperty.call(payload, 'data') ? payload.data : payload
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  if (Array.isArray(data.rows)) return data.rows
  if (Array.isArray(data.records)) return data.records
  if (Array.isArray(data.list)) return data.list
  if (Array.isArray(data.data)) return data.data
  if (Array.isArray(data.invoiceHeadList)) return data.invoiceHeadList
  if (Array.isArray(data.invoiceHeads)) return data.invoiceHeads
  if (Array.isArray(data.invoiceHeadVoList)) return data.invoiceHeadVoList
  return []
}

function flattenInvoiceHeadRow(raw) {
  if (!raw || typeof raw !== 'object') return {}
  const nestKeys = ['invoiceHead', 'headInfo', 'head', 'invoiceHeadVo', 'invoiceHeadInfo', 'vo', 'detail']
  let merged = { ...raw }
  nestKeys.forEach(key => {
    const inner = raw[key]
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      merged = { ...merged, ...inner }
    }
  })
  return merged
}

function pickFromHead(head, keys) {
  const row = head && typeof head === 'object' ? head : {}
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    const snake = toSnakeCaseKey(key)
    const candidates = snake && snake !== key ? [key, snake] : [key]
    for (let j = 0; j < candidates.length; j += 1) {
      const value = row[candidates[j]]
      if (value != null && String(value).trim() !== '') return String(value).trim()
    }
  }
  return ''
}

function emptyHeadFields() {
  return {
    invoiceTitle: '',
    enterpriseTaxNumber: '',
    openBankName: '',
    openBankAccount: '',
    registeredAddress: '',
    registeredTelephone: '',
    addressee: '',
    deliveryAddress: '',
    receivingContact: '',
    receivingContactInformation: ''
  }
}

function mapHeadRowToForm(head) {
  return {
    invoiceTitle: pickFromHead(head, ['invoiceTitle', 'invoiceHead', 'invoiceHeadName', 'title', 'companyName', 'headName', 'invoiceName', 'buyerName', 'purchaserName', 'orgName', 'customerName', 'unitName', 'ghdwmc', 'dwmc']),
    enterpriseTaxNumber: pickFromHead(head, ['enterpriseTaxNumber', 'taxNumber', 'taxNo', 'creditCode', 'unifiedSocialCreditCode', 'nsrsbh', 'dutyParagraph', 'taxpayerId', 'identificationNumber', 'taxId']),
    openBankName: pickFromHead(head, ['openBankName', 'bankName', 'depositBank', 'bankBranchName', 'khhmc']),
    openBankAccount: pickFromHead(head, ['openBankAccount', 'bankAccount', 'bankAccountNo', 'accountNo', 'yhzh']),
    registeredAddress: pickFromHead(head, ['registeredAddress', 'address', 'regAddress', 'registerAddress', 'zcdz']),
    registeredTelephone: pickFromHead(head, ['registeredTelephone', 'regPhone', 'phone', 'telephone', 'registerPhone', 'zcdh']),
    addressee: pickFromHead(head, ['addressee', 'receiverName', 'consignee', 'recipientName', 'sjrxm']),
    deliveryAddress: pickFromHead(head, ['deliveryAddress', 'mailAddress', 'receiveAddress', 'shippingAddress', 'sjdz']),
    receivingContact: pickFromHead(head, ['receivingContact', 'receiveEmail', 'email', 'sjyx']),
    receivingContactInformation: pickFromHead(head, ['receivingContactInformation', 'receivePhone', 'contactPhone', 'mobile', 'sjhm', 'linkPhone'])
  }
}

function requiredPatternValidator(message, pattern, patternMessage) {
  return (rule, value, callback) => {
    const valueText = String(value || '').trim()
    if (!valueText) {
      callback(new Error(message))
      return
    }
    if (!pattern.test(valueText)) {
      callback(new Error(patternMessage))
      return
    }
    callback()
  }
}

const taxNumberValidator = requiredPatternValidator('请输入企业税号', REG_ENTERPRISE_TAX_NUMBER, '企业税号格式不正确')
const bankAccountValidator = requiredPatternValidator('请输入开户行账号', REG_BANK_ACCOUNT, '开户行账号应为 8～30 位数字')
const registeredPhoneValidator = requiredPatternValidator('请输入注册电话', REG_REGISTERED_PHONE, '注册电话格式不正确')
const emailValidator = requiredPatternValidator('请输入收件邮箱', REG_EMAIL, '收件邮箱格式不正确')
const mobileContactValidator = requiredPatternValidator('请输入收件联系方式', REG_MOBILE, '收件联系方式请输入 11 位手机号')

export default {
  name: 'MallInvoiceApply',
  data() {
    return {
      pageLoading: false,
      submitting: false,
      orderIds: [],
      subjectType: '',
      invoiceTotalMoney: 0,
      defaultInvoiceHead: null,
      form: {
        invoiceType: 1,
        ifDefault: 2,
        ...emptyHeadFields()
      }
    }
  },
  computed: {
    hasDefaultInvoiceHead() {
      return this.defaultInvoiceHead != null && typeof this.defaultInvoiceHead === 'object'
    },
    invoiceAmountText() {
      return Number(this.invoiceTotalMoney || 0).toFixed(2)
    },
    formRules() {
      const specialRequired = this.form.invoiceType === 2
      return {
        invoiceTitle: [{ required: true, message: '请输入发票抬头', trigger: 'blur' }],
        enterpriseTaxNumber: [{ required: true, validator: taxNumberValidator, trigger: 'blur' }],
        openBankName: specialRequired ? [{ required: true, message: '请输入开户行名称', trigger: 'blur' }] : [],
        openBankAccount: specialRequired ? [{ required: true, validator: bankAccountValidator, trigger: 'blur' }] : [],
        registeredAddress: specialRequired ? [{ required: true, message: '请输入注册地址', trigger: 'blur' }] : [],
        registeredTelephone: specialRequired ? [{ required: true, validator: registeredPhoneValidator, trigger: 'blur' }] : [],
        addressee: specialRequired ? [{ required: true, message: '请输入收件人姓名', trigger: 'blur' }] : [],
        deliveryAddress: specialRequired ? [{ required: true, message: '请输入收件地址', trigger: 'blur' }] : [],
        receivingContact: specialRequired ? [{ required: true, validator: emailValidator, trigger: 'blur' }] : [],
        receivingContactInformation: specialRequired ? [{ required: true, validator: mobileContactValidator, trigger: 'blur' }] : []
      }
    }
  },
  watch: {
    'form.invoiceType'() {
      this.$nextTick(() => {
        if (this.$refs.invoiceForm) this.$refs.invoiceForm.clearValidate()
      })
    },
    'form.ifDefault'(value) {
      if (value === 2) {
        this.clearInvoiceHeadFields()
        this.$nextTick(() => {
          if (this.$refs.invoiceForm) this.$refs.invoiceForm.clearValidate()
        })
        return
      }
      if (value === 1) {
        if (!this.defaultInvoiceHead) {
          this.$message.warning('暂无默认发票抬头，请先维护抬头或选择「填写发票抬头」')
          this.$nextTick(() => {
            this.form.ifDefault = 2
          })
          return
        }
        this.$nextTick(() => {
          if (this.form.ifDefault !== 1) return
          this.applyDefaultHeadFromStore()
          if (this.$refs.invoiceForm) this.$refs.invoiceForm.clearValidate()
        })
      }
    }
  },
  methods: {
    init(data) {
      this.orderIds = data.orderIds
      this.subjectType = data.subjectType
      this.invoiceTotalMoney = data.invoiceTotalMoney
      this.pageLoading = false
      this.submitting = false
      this.defaultInvoiceHead = null
      this.form = {
        invoiceType: 1,
        ifDefault: 2,
        ...emptyHeadFields()
      }
      this.$nextTick(() => {
        if (this.$refs.invoiceForm) this.$refs.invoiceForm.clearValidate()
      })
      this.bootstrap()
    },
    goBack() {
      this.$emit('goBack')
    },
    patchHeadFields(patch) {
      Object.keys(emptyHeadFields()).forEach(key => {
        if (Object.prototype.hasOwnProperty.call(patch, key)) {
          this.$set(this.form, key, patch[key] != null ? String(patch[key]) : '')
        }
      })
    },
    clearInvoiceHeadFields() {
      this.patchHeadFields(emptyHeadFields())
    },
    applyDefaultHeadFromStore() {
      if (!this.defaultInvoiceHead) return
      this.patchHeadFields(mapHeadRowToForm(flattenInvoiceHeadRow(this.defaultInvoiceHead)))
    },
    onInvoiceTypeChange() {
      this.$nextTick(() => {
        if (this.$refs.invoiceForm) this.$refs.invoiceForm.clearValidate()
      })
    },
    async bootstrap() {
      const user = parseClient()
      if (!user.clientId) {
        this.$message.error('未获取到用户信息，请重新登录')
        this.goBack()
        return
      }
      this.pageLoading = true
      try {
        const headRes = await listInvoiceHead({ clientId: user.clientId, isDefault: 1 })
        const headList = extractList(headRes)
        this.defaultInvoiceHead = headList.length ? headList[0] : null
        if(!this.defaultInvoiceHead) {
          this.$confirm('您还未添加过发票抬头，是否前往添加？', '温馨提示').then(() => {
            this.$router.push({
              path: '/aboutUsIndex',
              query: {
                tabIndex: '3-3',
              }
            })
          })
        }
      } catch (error) {
        this.$message.error((error && error.message) || '加载失败')
      } finally {
        this.pageLoading = false
      }
    },
    handleSubmit() {
      if (!this.$refs.invoiceForm) return
      this.$refs.invoiceForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        const payload = {
          orderIds: this.orderIds,
          subjectType: this.subjectType,
          invoiceType: this.form.invoiceType,
          ifDefault: this.form.ifDefault,
          invoiceTitle: this.form.invoiceTitle,
          enterpriseTaxNumber: this.form.enterpriseTaxNumber,
          openBankName: this.form.openBankName,
          openBankAccount: this.form.openBankAccount,
          registeredAddress: this.form.registeredAddress,
          registeredTelephone: this.form.registeredTelephone,
          addressee: this.form.addressee,
          deliveryAddress: this.form.deliveryAddress,
          receivingContact: this.form.receivingContact,
          receivingContactInformation: this.form.receivingContactInformation
        }
        try {
          await submitMallOrderInvoice(payload)
          this.$message.success('开票申请已提交')
          this.$emit('backAndRefresh')
        } catch (error) {
          return null
        } finally {
          this.submitting = false
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.container {
  height: calc(100vh - 100px);
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

.mall-invoice-apply-page {
  width: 100%;
  min-height: 700px;
  box-sizing: border-box;
  background: #fff;
}

.invoice-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.invoice-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #111;
}

.go-orders-btn {
  color: #4d6fff;
}

.panel {
  background: #fff;
  border-top: 1px solid #e8e8e8;
  margin-top: 15px;
  padding: 22px 18px;
}

.form-section {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 22px;
  flex-wrap: wrap;
}

.form-section.block {
  align-items: flex-start;
}

.section-label {
  flex: 0 0 120px;
  width: 120px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
}

.section-label.align-top {
  padding-top: 8px;
}

.section-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.amount-display {
  font-size: 22px;
  font-weight: 700;
  color: #ff4a2b;
}

.pill-radio ::v-deep .el-radio-button__inner {
  border-radius: 2px;
  padding: 10px 18px;
  font-weight: 500;
}

.pill-radio ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #eef3ff;
  border-color: #4d6fff;
  color: #4d6fff;
  box-shadow: -1px 0 0 0 #4d6fff;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
  margin-top: 4px;
}

.grid-item {
  margin-bottom: 0;
}

.invoice-form ::v-deep .grid-item .el-form-item__label {
  float: none;
  display: block;
  text-align: left;
  line-height: 1.4;
  padding: 0 0 6px;
}

.invoice-form ::v-deep .grid-item .el-form-item__content {
  margin-left: 0 !important;
}

.invoice-form ::v-deep .el-input__inner {
  border-radius: 2px;
  height: 38px;
}

.footer-actions {
  margin-top: 18px;
  padding-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 50px;
}

.footer-actions ::v-deep .el-button {
  width: 160px;
  min-width: 160px;
}

.ghost-btn {
  border-radius: 2px;
  border-color: #dcdfe6;
  color: #333;
}

@media (max-width: 768px) {
  .mall-invoice-apply-page {
    width: 94%;
  }
  .invoice-top {
    gap: 8px;
    align-items: flex-start;
    flex-direction: column;
  }
  .form-section {
    flex-direction: column;
    align-items: stretch;
  }
  .section-label {
    text-align: left;
    flex: none;
    width: auto;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
